<template>
    <div class="resetPasswordContainer">
        <div class="bg-surface-0 w-full md:w-5/12 p-8 md:p-14">
            <div class="flex flex-col mb-5 text-center">
                <StaticImage imageClass="datalaneLogo m-auto mb-3" filename="pac-logo.svg"/>
                <div class="text-surface-900 dark:text-surface-0 text-3xl font-medium">Set your password</div>
                <div class="text-surface-600 mt-2" v-if="email">for {{ email }}</div>
            </div>

            <div v-if="loading" class="statusBox">
                <ProgressSpinner style="width: 2rem; height: 2rem" strokeWidth="6"/>
                <div>Validating reset link...</div>
            </div>

            <div v-else-if="globalError" class="statusBox">
                <Message severity="error">{{ globalError }}</Message>
                <Button class="mt-3" label="Back to Login" rounded @click="goToLogin"/>
            </div>

            <div v-else-if="success" class="statusBox">
                <Message severity="success">Password updated successfully.</Message>
                <Button class="mt-3" label="Go to Login" rounded @click="goToLogin"/>
            </div>

            <div v-else>
                <label for="new-password" class="block text-surface-900 dark:text-surface-0 font-medium mb-2">
                    New Password
                </label>
                <InputText
                    id="new-password"
                    type="password"
                    placeholder="New password"
                    class="w-full mb-3"
                    v-model="password"
                />

                <label for="confirm-password" class="block text-surface-900 dark:text-surface-0 font-medium mb-2">
                    Confirm Password
                </label>
                <InputText
                    id="confirm-password"
                    type="password"
                    placeholder="Confirm password"
                    class="w-full mb-3"
                    v-model="confirmPassword"
                    @keyup.enter="setPassword"
                />

                <div class="policyList" v-if="policyChecks.length > 0">
                    <div class="policyTitle">Password requirements</div>
                    <div
                        v-for="rule in policyChecks"
                        :key="rule.label"
                        :class="rule.valid ? 'policyRule ok' : 'policyRule ko'"
                    >
                        <i :class="rule.valid ? 'pi pi-check-circle policyRuleIcon' : 'pi pi-times-circle policyRuleIcon'"/>
                        {{ rule.label }}
                    </div>
                </div>

                <Message v-if="passwordMismatch" severity="warn" class="mb-3">Passwords do not match.</Message>
                <Message v-if="submitError" severity="error" class="mb-3">{{ submitError }}</Message>

                <Button
                    label="Set Password"
                    rounded
                    class="w-full"
                    @click="setPassword"
                    :loading="submitting"
                    :disabled="!isFormValid || submitting"
                />
            </div>
        </div>

        <div class="hidden md:block w-7/12 bg-no-repeat bg-cover"
             style="background-image: linear-gradient(to right bottom, #FF6101, #cc612e, #a04532, #712f2e, #421e22, #341b22, #26181f, #191518, #242027, #2b2c38, #2f3949, #2f4858);"></div>
    </div>
</template>

<script setup>
import {computed, onMounted, ref, watch} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {confirmPasswordReset, verifyPasswordResetCode} from 'firebase/auth';
import {auth} from '@/firebaseConfig';
import {ROUTE_NAMES} from '@/src/router';

const route = useRoute();
const router = useRouter();

const loading = ref(true);
const submitting = ref(false);
const success = ref(false);
const email = ref('');
const password = ref('');
const confirmPassword = ref('');
const globalError = ref(null);
const submitError = ref(null);

const oobCode = computed(() => String(route.query.oobCode || ''));
const mode = computed(() => String(route.query.mode || ''));
const passwordMismatch = computed(() => confirmPassword.value.length > 0 && password.value !== confirmPassword.value);
const hasMinLength = computed(() => password.value.length >= 6);
const hasLowercase = computed(() => /[a-z]/.test(password.value));
const hasUppercase = computed(() => /[A-Z]/.test(password.value));
const hasNumber = computed(() => /[0-9]/.test(password.value));
const hasSpecialCharacter = computed(() => /[^A-Za-z0-9]/.test(password.value));
const isPasswordValid = computed(() =>
    hasMinLength.value &&
    hasLowercase.value &&
    hasUppercase.value &&
    hasNumber.value &&
    hasSpecialCharacter.value
);
const isFormValid = computed(() => Boolean(password.value) && !passwordMismatch.value && isPasswordValid.value);

const policyChecks = computed(() => {
    return [
        {label: 'Minimum 6 characters', valid: hasMinLength.value},
        {label: 'At least one lowercase letter', valid: hasLowercase.value},
        {label: 'At least one uppercase letter', valid: hasUppercase.value},
        {label: 'At least one number', valid: hasNumber.value},
        {label: 'At least one special character', valid: hasSpecialCharacter.value},
    ];
});

const goToLogin = async () => {
    await router.push({name: ROUTE_NAMES.LOGIN});
};

const setPassword = async () => {
    if (!isFormValid.value || !oobCode.value) {
        return;
    }

    submitError.value = null;
    submitting.value = true;
    try {
        await confirmPasswordReset(auth, oobCode.value, password.value);
        success.value = true;
    } catch (error) {
        console.log(error);
        submitError.value = 'Unable to set password. The link may be expired, invalid, or the password does not satisfy policy.';
    } finally {
        submitting.value = false;
    }
};

watch(password, () => {
    submitError.value = null;
});

onMounted(async () => {
    if (!oobCode.value || (mode.value && mode.value !== 'resetPassword')) {
        globalError.value = 'Invalid reset link.';
        loading.value = false;
        return;
    }

    try {
        email.value = await verifyPasswordResetCode(auth, oobCode.value);
    } catch (error) {
        console.log(error);
        globalError.value = 'This reset link is invalid or has expired.';
    } finally {
        loading.value = false;
    }
});
</script>

<style scoped>
.resetPasswordContainer {
    min-height: 100%;
    display: flex;
}

:deep(.datalaneLogo) {
    height: 100px;
    width: 250px;
}

.statusBox {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
}

.policyList {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0.75rem;
    margin-bottom: 1rem;
}

.policyTitle {
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.policyRule {
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
}

.policyRuleIcon {
    margin-right: 0.5rem;
}

.policyRule.ok {
    color: #047857;
}

.policyRule.ko {
    color: #b91c1c;
}
</style>
